import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Accordion } from "react-bootstrap";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppLoader } from "src/_common/hooks/actions/common/appLoaderHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import "./AIInterviewReportModal.css";

interface QuestionReview {
  complexity: string;
  critical_thinking: string;
  feedback: string;
  grammar: string;
  keywords: string;
  recommended_answer: string;
  score: string;
  spelling: string;
  technical_terms: string;
}

interface ReviewData {
  [key: string]: QuestionReview;
}

interface OverallAssessment {
  attitude: string;
  average_score: string;
  communication: string;
  confidence: string;
  cultural_fit: string;
  problem_solving: string;
  role_related_knowledge: string;
}

interface InterviewSession {
  _id?: string;
  interview_schedule_id?: string;
  job_id?: string;
  jobseeker_id?: number;
  status?: string;
  channel_name?: string;
  started_at?: string;
  ended_at?: string;
  rtc_token?: string;
  rtc_uid?: number;
  token_expires_at?: string;
  token_generated_at?: string | null;
  recording_resource_id?: string;
  recording_sid?: string;
  recording_started_at?: string | null;
  recording_uid?: number;
  recording_stopped_at?: string | null;
  file_list?: any[];
  created_at?: string;
  updated_at?: string;
  job_title?: string;
  company_name?: string;
  location?: string;
  duration?: string;
  scheduled_at?: string;
}

interface JobDetails {
  job_emp_id?: string;
  job_title?: string;
  job_desc?: string;
  skill_list?: string;
  skill_list_ar?: string;
  workplace_type?: string;
  job_type?: string;
  country_name?: string;
  country_id?: number;
  company_id?: number;
  country_header_code?: string;
}

interface JobseekerBasicDetails {
  name?: string;
  email?: string;
  phone?: string;
  current_location?: string;
  current_company?: string;
  current_designation?: string;
}

interface Screening {
  job_id?: string;
  jobseeker_id?: number;
  type?: number;
  status?: number;
  matching_percentage?: number;
  job_details?: JobDetails;
  jobseeker_details?: Array<{
    basic_details?: JobseekerBasicDetails[];
  }>;
  company_id?: number;
}

interface ReportData {
  _id: string;
  interview_schedule_id: string;
  interview_session_id: string;
  jobseeker_id: number;
  questions: { [key: string]: string };
  answers: { [key: string]: string };
  overall_assessment: OverallAssessment;
  review_data: ReviewData;
  status: string;
  created_at: string;
  updated_at: string;
  interview_session: InterviewSession | null;
  screening?: Screening;
}

interface AIInterviewReportModalProps {
  show: boolean;
  onHide: () => void;
  scheduleId: string;
}

const AIInterviewReportModal: React.FC<AIInterviewReportModalProps> = ({ show, onHide, scheduleId }) => {
  const interviewApi = useInterviewApi();
  const toast = useToaster();
  const { showLoader, hideLoader } = useAppLoader();
  const userDetails = useAppUserDetailsSelector();
  const { t } = useTranslation("ai-interview-report");

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [processedData, setProcessedData] = useState<any>(null);
  const [expandAllAccordions, setExpandAllAccordions] = useState(false);
  const [activeAccordions, setActiveAccordions] = useState<{ [key: number]: string[] }>({});
  const reportContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show && scheduleId) {
      fetchInterviewEvaluation();
    }
  }, [show, scheduleId]);

  const fetchInterviewEvaluation = () => {
    showLoader();
    interviewApi.callGetInterviewEvaluation(
      { schedule_id: scheduleId },
      (message: string, resp: any) => {
        console.log("Interview evaluation fetched:", resp);
        setReportData(resp);
        processReportData(resp);
        hideLoader();
      },
      (error: string) => {
        console.error("Error fetching interview evaluation:", error);
        toast.error(error);
        onHide();
        hideLoader();
      }
    );
  };

  const processReportData = (data: ReportData) => {
    if (!data) return;

    const questionKeys = Object.keys(data.questions).sort();
    console.log("Processing questions with keys:", questionKeys);

    const categorizedQuestions: any = {
      introduction: [],
      situational: [],
      behavioral: [],
      technical: [],
    };

    questionKeys.forEach((key) => {
      const question = data.questions[key];
      const answer = data.answers[key];
      const reviewKey = `Question${key}`;
      const review = data.review_data[reviewKey] || data.review_data[key];

      console.log(`Processing question ${key}:`, { question, answer, review });

      if (!question || !answer) return;

      const questionData = {
        question,
        answer,
        review: review || {},
      };

      const questionLower = question.toLowerCase();
      if (questionLower.includes("motivat") || questionLower.includes("apply") || key === "1") {
        categorizedQuestions.introduction.push(questionData);
      } else if (
        questionLower.includes("situati") ||
        questionLower.includes("imagin") ||
        questionLower.includes("scenario")
      ) {
        categorizedQuestions.situational.push(questionData);
      } else if (
        questionLower.includes("example") ||
        questionLower.includes("experience") ||
        questionLower.includes("time when") ||
        questionLower.includes("describe")
      ) {
        categorizedQuestions.behavioral.push(questionData);
      } else {
        categorizedQuestions.technical.push(questionData);
      }
    });

    console.log("Categorized questions:", categorizedQuestions);
    setProcessedData(categorizedQuestions);
  };

  const handleDownload = async () => {
    if (!reportData || !reportContentRef.current) {
      toast.error(t("aiInterviewReport.messages.noData"));
      return;
    }

    try {
      showLoader();
      setExpandAllAccordions(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const html2canvas = (await import("html2canvas" as any)).default;
      const jsPDF = (await import("jspdf" as any)).jsPDF;

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      const questionCards = reportContentRef.current.querySelectorAll(".question-card");

      if (questionCards.length === 0) {
        toast.error(t("aiInterviewReport.messages.noQuestions"));
        hideLoader();
        return;
      }

      let yOffset = margin;
      let isFirstPage = true;

      const candidateName = reportData.screening?.jobseeker_details?.[0]?.basic_details?.[0]?.name || "Candidate";
      const jobTitle = reportData.screening?.job_details?.job_title || "Position";

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(t("aiInterviewReport.header.title"), margin, yOffset);
      yOffset += 8;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(`${t("aiInterviewReport.overview.candidate")}: ${candidateName}`, margin, yOffset);
      yOffset += 5;
      pdf.text(`${t("aiInterviewReport.overview.jobTitle")}: ${jobTitle}`, margin, yOffset);
      yOffset += 10;

      for (let i = 0; i < questionCards.length; i++) {
        const card = questionCards[i] as HTMLElement;

        const canvas = await html2canvas(card, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (yOffset + imgHeight > pageHeight - margin) {
          pdf.addPage();
          yOffset = margin;
          isFirstPage = false;
        }

        pdf.addImage(imgData, "PNG", margin, yOffset, imgWidth, imgHeight);
        yOffset += imgHeight + 5;
      }

      pdf.save(`AI_Interview_Report_${candidateName}_${Date.now()}.pdf`);
      toast.success(t("aiInterviewReport.messages.downloadSuccess"));

      setExpandAllAccordions(false);
      hideLoader();
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("aiInterviewReport.messages.downloadError"));
      setExpandAllAccordions(false);
      hideLoader();
    }
  };

  const parseScore = (scoreStr: string | undefined): number => {
    if (!scoreStr) return 0;
    const match = scoreStr.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const renderAttributeBar = (label: string, value: number, max: number) => {
    const percentage = (value / max) * 100;
    return (
      <div className="attribute-bar-item">
        <div className="attribute-label">
          <span>{label}</span>
          <span className="attribute-value">
            {value}/{max}
          </span>
        </div>
        <div className="attribute-bar-track">
          <div
            className="attribute-bar-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor: percentage >= 70 ? "#38A169" : percentage >= 40 ? "#faa43a" : "#ef4444",
            }}
          />
        </div>
      </div>
    );
  };

  const renderQuestionCard = (questionData: any, index: number) => {
    const score = parseScore(questionData.review?.score);
    const maxScore = 10;
    const percentage = (score / maxScore) * 100;

    const getScoreClass = (percentage: number) => {
      if (percentage >= 70) return "high";
      if (percentage >= 40) return "medium";
      return "low";
    };

    const toggleAccordion = (cardIndex: number, section: string) => {
      setActiveAccordions((prev) => {
        const cardAccordions = prev[cardIndex] || [];
        if (cardAccordions.includes(section)) {
          return {
            ...prev,
            [cardIndex]: cardAccordions.filter((s) => s !== section),
          };
        } else {
          return {
            ...prev,
            [cardIndex]: [...cardAccordions, section],
          };
        }
      });
    };

    const isAccordionActive = (cardIndex: number, section: string) => {
      return expandAllAccordions || (activeAccordions[cardIndex] || []).includes(section);
    };

    return (
      <div key={index} className="question-card">
        <div className="question-header">
          <div className="question-number">
            <i className="fa fa-question-circle"></i>
            <span>
              {t("aiInterviewReport.detailReview.question")} {index + 1}
            </span>
          </div>
          <div className={`score-badge ${getScoreClass(percentage)}`}>
            {score.toFixed(1)}/{maxScore}
          </div>
        </div>

        <div className="question-text">{questionData.question}</div>

        <div className="answer-section">
          <div className="section-label">
            <i className="fa fa-comment"></i>
            {t("aiInterviewReport.detailReview.candidateAnswer")}
          </div>
          <div className="answer-text">{questionData.answer}</div>
        </div>

        {questionData.review && (
          <div className="review-sections">
            <Accordion>
              <Accordion.Item eventKey="feedback">
                <Accordion.Header onClick={() => toggleAccordion(index, "feedback")}>
                  <i className={`fa fa-chevron-${isAccordionActive(index, "feedback") ? "down" : "right"}`}></i>
                  {t("aiInterviewReport.detailReview.feedback")}
                </Accordion.Header>
                <Accordion.Body className={isAccordionActive(index, "feedback") ? "show" : ""}>
                  {questionData.review.feedback || t("aiInterviewReport.detailReview.noFeedback")}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="recommended">
                <Accordion.Header onClick={() => toggleAccordion(index, "recommended")}>
                  <i className={`fa fa-chevron-${isAccordionActive(index, "recommended") ? "down" : "right"}`}></i>
                  {t("aiInterviewReport.detailReview.recommendedAnswer")}
                </Accordion.Header>
                <Accordion.Body className={isAccordionActive(index, "recommended") ? "show" : ""}>
                  {questionData.review.recommended_answer || t("aiInterviewReport.detailReview.noRecommendation")}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="assessment">
                <Accordion.Header onClick={() => toggleAccordion(index, "assessment")}>
                  <i className={`fa fa-chevron-${isAccordionActive(index, "assessment") ? "down" : "right"}`}></i>
                  {t("aiInterviewReport.detailReview.detailedAssessment")}
                </Accordion.Header>
                <Accordion.Body className={isAccordionActive(index, "assessment") ? "show" : ""}>
                  <div className="assessment-grid">
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.complexity")}:</strong>
                      <span>{questionData.review.complexity || "N/A"}</span>
                    </div>
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.criticalThinking")}:</strong>
                      <span>{questionData.review.critical_thinking || "N/A"}</span>
                    </div>
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.grammar")}:</strong>
                      <span>{questionData.review.grammar || "N/A"}</span>
                    </div>
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.spelling")}:</strong>
                      <span>{questionData.review.spelling || "N/A"}</span>
                    </div>
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.keywords")}:</strong>
                      <span>{questionData.review.keywords || "N/A"}</span>
                    </div>
                    <div className="assessment-item">
                      <strong>{t("aiInterviewReport.detailReview.technicalTerms")}:</strong>
                      <span>{questionData.review.technical_terms || "N/A"}</span>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        )}
      </div>
    );
  };

  if (!reportData) {
    return null;
  }

  const overallScore = parseScore(reportData.overall_assessment?.average_score);
  const attitude = parseScore(reportData.overall_assessment?.attitude);
  const communication = parseScore(reportData.overall_assessment?.communication);
  const confidence = parseScore(reportData.overall_assessment?.confidence);
  const culturalFit = parseScore(reportData.overall_assessment?.cultural_fit);
  const problemSolving = parseScore(reportData.overall_assessment?.problem_solving);
  const roleKnowledge = parseScore(reportData.overall_assessment?.role_related_knowledge);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="ai-interview-report-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="modal-title-section">
            <h2>{t("aiInterviewReport.header.title")}</h2>
            <p className="modal-subtitle">{t("aiInterviewReport.header.subtitle")}</p>
          </div>
        </Modal.Title>
        <div className="modal-header-actions">
          <button className="action-btn" onClick={handleDownload} title={t("aiInterviewReport.actions.download")}>
            <i className="fa fa-download"></i>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div ref={reportContentRef} className="ai-interview-report-modal-content">
          <div className="overview-section">
            <h3 className="section-title">{t("aiInterviewReport.overview.title")}</h3>

            <div className="overview-grid">
              <div className="overview-details">
                {reportData.screening?.job_details?.job_title && (
                  <div className="detail-item">
                    <i className="fa fa-briefcase"></i>
                    <div>
                      <span className="detail-label">{t("aiInterviewReport.overview.jobTitle")}</span>
                      <span className="detail-value">{reportData.screening.job_details.job_title}</span>
                    </div>
                  </div>
                )}

                {userDetails?.company?.name && (
                  <div className="detail-item">
                    <i className="fa fa-building"></i>
                    <div>
                      <span className="detail-label">{t("aiInterviewReport.overview.company")}</span>
                      <span className="detail-value">{userDetails.company.name}</span>
                    </div>
                  </div>
                )}

                {reportData.screening?.job_details?.country_name && (
                  <div className="detail-item">
                    <i className="fa fa-map-marker-alt"></i>
                    <div>
                      <span className="detail-label">{t("aiInterviewReport.overview.location")}</span>
                      <span className="detail-value">{reportData.screening.job_details.country_name}</span>
                    </div>
                  </div>
                )}

                {reportData.interview_session?.started_at && (
                  <div className="detail-item">
                    <i className="fa fa-calendar"></i>
                    <div>
                      <span className="detail-label">{t("aiInterviewReport.overview.interviewSession")}</span>
                      <span className="detail-value">
                        {new Date(reportData.interview_session.started_at).toLocaleString("en-US", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {overallScore > 0 && (
                <div className="overview-center">
                  <div className="ai-score-circle">
                    <div className="circle-container">
                      <svg viewBox="0 0 120 120" className="score-svg">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" strokeWidth="10" />
                        <circle
                          cx="60"
                          cy="60"
                          r="50"
                          fill="none"
                          stroke="#faa43a"
                          strokeWidth="10"
                          strokeDasharray={`${(overallScore / 10) * 314} 314`}
                          strokeDashoffset="0"
                          transform="rotate(-90 60 60)"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="score-text">
                        <span className="score-number">{Math.round(overallScore * 10)}%</span>
                        <span className="score-label">
                          {overallScore} /10
                          <br />
                          {t("aiInterviewReport.overview.avgScore")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {reportData.overall_assessment && (
                <div className="overview-right">
                  <div className="attributes-section">
                    <div className="attributes-grid">
                      {attitude > 0 && renderAttributeBar(t("aiInterviewReport.attributes.attitude"), attitude, 10)}
                      {communication > 0 &&
                        renderAttributeBar(t("aiInterviewReport.attributes.communication"), communication, 10)}
                      {confidence > 0 &&
                        renderAttributeBar(t("aiInterviewReport.attributes.confidence"), confidence, 10)}
                      {culturalFit > 0 &&
                        renderAttributeBar(t("aiInterviewReport.attributes.culturalFit"), culturalFit, 10)}
                      {problemSolving > 0 &&
                        renderAttributeBar(t("aiInterviewReport.attributes.problemSolving"), problemSolving, 10)}
                      {roleKnowledge > 0 &&
                        renderAttributeBar(t("aiInterviewReport.attributes.roleKnowledge"), roleKnowledge, 10)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {processedData && (
            <div className="detail-review-section">
              <h3 className="section-title">{t("aiInterviewReport.detailReview.title")}</h3>

              {processedData.introduction.length > 0 &&
                processedData.introduction.map((q: any, i: number) => renderQuestionCard(q, i))}

              {processedData.situational.length > 0 &&
                processedData.situational.map((q: any, i: number) => renderQuestionCard(q, i))}

              {processedData.behavioral.length > 0 &&
                processedData.behavioral.map((q: any, i: number) => renderQuestionCard(q, i))}

              {processedData.technical.length > 0 &&
                processedData.technical.map((q: any, i: number) => renderQuestionCard(q, i))}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AIInterviewReportModal;
